import { useState } from "react";
import { formatEther } from "ethers";
import { formatContractError } from "@/lib/error";
import { useContracts } from "@/hooks/useContracts";
import { useGlobalStats } from "@/hooks/useGlobalStats";
import { getContracts, getProvider } from "@/lib/contracts";
import { refreshAfterTx } from "@/lib/refreshAll";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Loader2 } from "lucide-react";

const MAX_PER_TX = 5;

interface MintProofData {
  weights: bigint[];
  uris: string[];
  proofs: string[][];
}

async function fetchMerkleProofs(startTokenId: number, quantity: number): Promise<MintProofData | null> {
  try {
    const res = await fetch("/merkle-proofs.json");
    if (!res.ok) return null;
    const all = (await res.json()) as Record<
      string,
      { tokenId: number; weight: number; uri: string; proof: string[] }
    >;
    const weights: bigint[] = [];
    const uris: string[] = [];
    const proofs: string[][] = [];
    for (let i = 0; i < quantity; i++) {
      const item = all[String(startTokenId + i)];
      if (!item) return null;
      weights.push(BigInt(item.weight));
      uris.push(item.uri);
      proofs.push(item.proof);
    }
    return { weights, uris, proofs };
  } catch {
    return null;
  }
}

interface MintControllerProps {
  onMinted?: () => void;
}

export function MintController({ onMinted }: MintControllerProps) {
  const { stats, isLoading: statsLoading } = useGlobalStats();
  const { nft, connected, requestConnect, account } = useContracts();
  const [quantity, setQuantity] = useState(1);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mintPriceWei = stats.mintPriceWei;
  const mintOpen = stats.mintOpen;
  const isReady = !statsLoading && mintPriceWei > 0n;
  const totalWei = mintPriceWei * BigInt(quantity);
  const totalEth = isReady ? formatEther(totalWei) : "—";

  const adjust = (delta: number) => {
    setQuantity((prev) => Math.max(1, Math.min(MAX_PER_TX, prev + delta)));
  };

  const handleMint = async () => {
    setError(null);

    if (!connected) {
      try {
        await requestConnect();
      } catch {
        setError("Wallet connection rejected");
      }
      return;
    }

    if (!mintOpen) {
      setError("Mint is closed");
      return;
    }

    setIsPending(true);
    try {
      const provider = getProvider();
      const { nft: readNft } = getContracts(provider);

      // 检查账户在链上是否有足够 ETH 付 mint 金额 + gas
      if (account) {
        const balance = await provider.getBalance(account);
        const feeData = await provider.getFeeData();
        const gasPrice = feeData.gasPrice ?? feeData.maxFeePerGas ?? 10n ** 9n;
        // 按当前链上 gas 价格的 2 倍预留 300k gas
        const reservedGas = 300_000n * gasPrice * 2n;
        if (balance < totalWei + reservedGas) {
          setError(
            `Insufficient ETH for account ${account.slice(0, 6)}...${account.slice(-4)}. Please send ETH to this address to continue.`
          );
          setIsPending(false);
          return;
        }
      }

      // 拉取 Merkle Proof：当前合约已用 Merkle Root 懒写入稀有度/URI，mint 时必须带 proof
      const totalMinted = Number(await readNft.totalMinted());
      const proofData = await fetchMerkleProofs(totalMinted + 1, quantity);
      if (!proofData) {
        setError("Failed to load mint proofs. Please try again.");
        setIsPending(false);
        return;
      }

      // 先静态调用，拿到更明确的 revert reason
      try {
        await readNft.mint.staticCall(
          quantity,
          proofData.weights,
          proofData.uris,
          proofData.proofs,
          { from: account ?? undefined, value: totalWei }
        );
      } catch (staticErr) {
        setError(formatContractError(staticErr));
        setIsPending(false);
        return;
      }

      // 用项目配置的 RPC 预估 gas，避免钱包对自定义链给出 50M 之类的高 gas limit
      const data = nft.interface.encodeFunctionData("mint", [
        quantity,
        proofData.weights,
        proofData.uris,
        proofData.proofs,
      ]);
      const gasEstimate = await provider.estimateGas({
        to: nft.target as string,
        from: account ?? undefined,
        data,
        value: totalWei,
      });
      const gasLimit = (gasEstimate * 120n) / 100n;

      // 使用链上实时 fee data，避免固定 100 gwei 导致余额不足误判
      const feeData = await provider.getFeeData();
      const maxFeePerGas = ((feeData.maxFeePerGas ?? feeData.gasPrice ?? 10n ** 9n) * 12n) / 10n;
      const maxPriorityFeePerGas = feeData.maxPriorityFeePerGas ?? 10n ** 8n;

      const tx = await nft.mint(quantity, proofData.weights, proofData.uris, proofData.proofs, {
        value: totalWei,
        gasLimit,
        maxFeePerGas,
        maxPriorityFeePerGas,
      });

      // OKX 等钱包的 provider 可能不返回 receipt，改用本地 RPC 等待确认
      const receipt = await provider.waitForTransaction(tx.hash, 1, 30000);
      if (!receipt) {
        throw new Error("Transaction not confirmed within 30s");
      }

      // 页面已无定时轮询，铸造成功后必须主动刷新，新猫才会出现在 My Cats
      await refreshAfterTx();
      onMinted?.();
    } catch (err) {
      setError(formatContractError(err));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-lg">
      <div className="flex items-center justify-between">
        <span className="font-pixel text-xs text-primary">MINT</span>
        <span className="text-xs text-muted-foreground">Max {MAX_PER_TX} per tx</span>
      </div>

      <div className="mt-4 flex items-center justify-between rounded-xl border border-border bg-background p-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => adjust(-1)}
          disabled={quantity <= 1 || isPending}
          aria-label="Decrease quantity"
        >
          <Minus size={18} />
        </Button>
        <span className="font-pixel text-xl text-foreground">{quantity}</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => adjust(1)}
          disabled={quantity >= MAX_PER_TX || isPending}
          aria-label="Increase quantity"
        >
          <Plus size={18} />
        </Button>
      </div>

      <div className="mt-4 flex items-end justify-between">
        <div>
          <p className="text-xs text-muted-foreground">Total</p>
          <p className="font-mono text-lg font-semibold text-foreground">{totalEth} ETH</p>
        </div>
        <Button
          variant="pixel"
          size="pixel"
          onClick={handleMint}
          disabled={isPending || !mintOpen || !isReady}
          className="min-w-[120px]"
        >
          {isPending ? (
            <span className="flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" />
              Minting
            </span>
          ) : connected ? (
            "Mint"
          ) : (
            "Connect & Mint"
          )}
        </Button>
      </div>

      {error && <p className="mt-3 text-xs text-danger">{error}</p>}
    </div>
  );
}
